import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { productService } from '../services/productService';
import { conversationService } from '../services/conversationService';
import { reviewService } from '../services/reviewService';
import { wishlistService } from '../services/wishlistService';
import { userService } from '../services/userService';
import { useAuth } from '../services/AuthContext';
import { extractIdFromSlug, productUrl } from '../utils/seo';
import Modal from '../components/Modal';
import UserLink from '../components/UserLink';
import ReviewForm from '../components/ReviewForm';
import ShareButtons from '../components/ShareButtons';

function Stars({ rating, size = '1rem' }) {
  return (
    <span className="stars" style={{ fontSize: size }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= Math.round(rating) ? 'star-filled' : 'star-empty'}>★</span>
      ))}
    </span>
  );
}

function Gallery({ images }) {
  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setFullscreen(false); setZoomed(false); }
      if (e.key === 'ArrowLeft') { setZoomed(false); prev(); }
      if (e.key === 'ArrowRight') { setZoomed(false); next(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen, prev, next]);

  if (!images?.length) return <div className="gallery-placeholder">📦</div>;

  return (
    <>
      <div className="gallery">
        <img
          src={images[idx]} alt="" className="gallery-main"
          onClick={() => setFullscreen(true)}
          style={{ cursor: 'zoom-in' }}
        />
        {images.length > 1 && (
          <>
            <button className="gallery-nav gallery-nav--prev" onClick={prev}>‹</button>
            <button className="gallery-nav gallery-nav--next" onClick={next}>›</button>
            <div className="gallery-dots">
              {images.map((_, i) => (
                <div key={i} className={`gallery-dot ${i === idx ? 'gallery-dot--active' : ''}`}
                  onClick={() => setIdx(i)} />
              ))}
            </div>
          </>
        )}
      </div>

      {fullscreen && (
        <div
          className="gallery-fs-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) { setFullscreen(false); setZoomed(false); } }}
        >
          <button className="gallery-fs-close" onClick={() => { setFullscreen(false); setZoomed(false); }}>✕</button>
          <img
            src={images[idx]} alt=""
            className={`gallery-fs-img${zoomed ? ' gallery-fs-img--zoomed' : ''}`}
            onClick={() => setZoomed((z) => !z)}
          />
          {images.length > 1 && (
            <>
              <button className="gallery-fs-nav gallery-fs-nav--prev" onClick={() => { setZoomed(false); prev(); }}>‹</button>
              <button className="gallery-fs-nav gallery-fs-nav--next" onClick={() => { setZoomed(false); next(); }}>›</button>
            </>
          )}
          <div className="gallery-fs-counter">{idx + 1} / {images.length}</div>
        </div>
      )}
    </>
  );
}

function ReviewsSection({ sellerUsername, currentUsername }) {
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [responding, setResponding] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    if (!loaded) {
      reviewService.getSellerReviews(sellerUsername)
        .then((res) => setReviews(res.data?.content || []))
        .catch(() => {})
        .finally(() => setLoaded(true));
    }
  };

  const toggle = () => { if (!open) load(); setOpen((v) => !v); };

  const handleRespond = async (id) => {
    if (!responseText.trim()) return;
    setSubmitting(true);
    try {
      const res = await reviewService.respond(id, responseText.trim());
      setReviews((prev) => prev.map((r) => r.id === id ? res.data : r));
      setResponding(null);
      setResponseText('');
    } catch {
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '1.25rem' }}>
      <button
        className="btn-text"
        style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        onClick={toggle}
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>Reseñas del vendedor {reviews.length > 0 && `(${reviews.length})`}</span>
      </button>

      {open && (
        <div style={{ marginTop: '1rem' }}>
          {!loaded ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>Cargando...</div>
          ) : reviews.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>
              Este vendedor no tiene reseñas todavía.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {reviews.map((r) => (
                <div key={r.id} className="card" style={{ padding: '0.85rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.4rem' }}>
                    <Stars rating={r.rating} />
                    <UserLink
                      username={r.reviewerUsername}
                      rating={r.reviewerRating}
                      reviewCount={r.reviewerReviewCount}
                    />
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginLeft: 'auto' }}>
                      {new Date(r.createdAt).toLocaleDateString('es-UY')}
                    </span>
                  </div>
                  {r.comment && (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.55, margin: 0 }}>
                      {r.comment}
                    </p>
                  )}
                  {r.sellerResponse && (
                    <div className="info-box" style={{ marginTop: '0.6rem', fontSize: '0.83rem' }}>
                      <strong>Respuesta:</strong> {r.sellerResponse}
                    </div>
                  )}
                  {!r.sellerResponse && currentUsername === sellerUsername && (
                    responding === r.id ? (
                      <div style={{ marginTop: '0.6rem' }}>
                        <textarea
                          className="chat-input"
                          style={{ borderRadius: 'var(--radius-md)', padding: '0.6rem 0.8rem', width: '100%', resize: 'vertical', minHeight: 64 }}
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          placeholder="Tu respuesta..."
                        />
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                          <button className="btn btn-primary btn-sm" disabled={submitting} onClick={() => handleRespond(r.id)}>
                            {submitting ? <span className="spinner" /> : 'Responder'}
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setResponding(null)}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <button className="btn-text btn-sm" style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}
                        onClick={() => { setResponding(r.id); setResponseText(''); }}>
                        Responder
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProductDetail() {
  const { slug } = useParams();
  const id = extractIdFromSlug(slug);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contacting, setContacting] = useState(false);
  const [interested, setInterested] = useState(false);
  const [interestCount, setInterestCount] = useState(0);
  const [togglingInterest, setTogglingInterest] = useState(false);
  const [unselling, setUnselling] = useState(false);
  const [unsellModal, setUnsellModal] = useState(false);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(null); // 'seller' or 'buyer'
  const [reviewSubmitted, setReviewSubmitted] = useState({});
  const [wishlisted, setWishlisted] = useState(false);
  const [togglingWishlist, setTogglingWishlist] = useState(false);
  const [wishlistTooltip, setWishlistTooltip] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    productService.getById(id)
      .then((res) => {
        setProduct(res.data);
        setInterested(res.data.userInterested || false);
        setInterestCount(res.data.interestCount || 0);
        setWishlisted(res.data.userWishlisted || false);
        // Redirect to canonical SEO URL if slug doesn't match
        const canonical = productUrl(res.data);
        if (window.location.pathname !== canonical) {
          navigate(canonical, { replace: true });
        }
        // Track category visit for authenticated users
        if (isAuthenticated && res.data.category) {
          userService.trackCategoryVisit(res.data.category).catch(() => {});
        }
      })
      .catch(() => setError('Producto no encontrado'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isAuthenticated && product?.status === 'SOLD' && product?.soldToUsername) {
      reviewService.getReviewStatus(id)
        .then((res) => setReviewStatus(res.data))
        .catch(() => {});
    }
  }, [id, isAuthenticated, product?.status, product?.soldToUsername]);

  const handleInterest = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setTogglingInterest(true);
    try {
      const res = await productService.toggleInterest(id);
      setInterested(res.data.interested);
      setInterestCount(res.data.count);
    } finally { setTogglingInterest(false); }
  };

  const handleWishlist = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setTogglingWishlist(true);
    try {
      const res = await wishlistService.toggle(id);
      const added = res.data.wishlisted;
      setWishlisted(added);
      if (added) {
        setWishlistTooltip(true);
        setTimeout(() => setWishlistTooltip(false), 5000);
      }
    } finally { setTogglingWishlist(false); }
  };

  const handleUnsell = () => setUnsellModal(true);

  const confirmUnsell = async () => {
    setUnsellModal(false);
    setUnselling(true);
    try {
      await productService.unsell(id);
      const res = await productService.getById(id);
      setProduct(res.data);
    } catch {
      setError('Error al deshacer la venta');
    } finally { setUnselling(false); }
  };

  const handleContact = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setContacting(true);
    try {
      const res = await conversationService.send({
        productId: Number(id),
        recipientUsername: product.sellerUsername,
        content: `Hola, estoy interesado en "${product.title}". ¿Sigue disponible?`,
      });
      navigate(`/messages/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 409) {
        const allRes = await conversationService.getAll();
        const existing = allRes.data.find((c) => String(c.productId) === String(id));
        if (existing) navigate(`/messages/${existing.id}`);
      } else {
        setError('Error al iniciar conversación');
      }
    } finally { setContacting(false); }
  };

  if (loading) return (
    <div className="loading">
      <div className="spinner" style={{ width: 36, height: 36, margin: '0 auto 1rem' }} />
      <p>Cargando...</p>
    </div>
  );

  if (error && !product) return (
    <div className="detail-container">
      <div className="error-state">{error}</div>
      <div style={{ textAlign: 'center', marginTop: '1rem' }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Volver</button>
      </div>
    </div>
  );

  if (!product) return null;

  const isOwner = user && user.username === product.sellerUsername;
  const characteristics = (() => {
    try { return product.characteristics ? JSON.parse(product.characteristics) : null; }
    catch { return null; }
  })();

  const waUrl = product.sellerWhatsapp
    ? `https://wa.me/${product.sellerWhatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola, vi tu publicación "${product.title}" en MercadoLibertario`)}`
    : null;

  const deliveryLabel = {
    PICKUP: 'Solo retiro en mano',
    SHIPPING: 'Envío incluido',
    BOTH: 'Retiro o envío',
  }[product.deliveryMethod] || '';

  const canonicalUrl = `${window.location.origin}${productUrl(product)}`;
  const ogImage = product.imageUrls?.[0] || `${window.location.origin}/logo512.png`;
  const metaDescription = product.description
    ? product.description.substring(0, 160)
    : `${product.title} en MercadoLibertario — Compra y vende libremente en Uruguay`;
  const priceText = product.priceOnConsultation
    ? 'Precio a consultar'
    : `${product.currency === 'USD' ? 'US$' : '$'}${Number(product.price).toLocaleString('es-UY')}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: product.description || '',
    image: product.imageUrls || [],
    url: canonicalUrl,
    ...(product.createdAt && { datePublished: product.createdAt }),
    ...(product.updatedAt && { dateModified: product.updatedAt }),
    offers: {
      '@type': 'Offer',
      priceCurrency: product.currency || 'UYU',
      price: product.priceOnConsultation ? undefined : product.price,
      availability: product.status === 'ACTIVE'
        ? 'https://schema.org/InStock'
        : product.status === 'SOLD'
          ? 'https://schema.org/SoldOut'
          : 'https://schema.org/Discontinued',
      url: canonicalUrl,
      seller: {
        '@type': 'Person',
        name: product.sellerUsername,
        url: `${window.location.origin}/profile/${product.sellerUsername}`,
      },
    },
    ...(product.condition && {
      itemCondition: product.condition === 'NEW'
        ? 'https://schema.org/NewCondition'
        : 'https://schema.org/UsedCondition',
    }),
    ...(product.sellerRating && product.sellerReviewCount > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.sellerRating.toFixed(1),
        reviewCount: product.sellerReviewCount,
        bestRating: 5,
        worstRating: 1,
      },
    }),
  };

  return (
    <div className="detail-container">
      <Helmet>
        <title>{product.title} — {priceText} — MercadoLibertario</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.title} — ${priceText}`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="MercadoLibertario" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.title} — ${priceText}`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {unsellModal && (
        <Modal
          type="warning"
          title="¿Deshacer la venta?"
          message="La publicación volverá al estado Activa y los interesados podrán verla nuevamente."
          onConfirm={confirmUnsell}
          onClose={() => setUnsellModal(false)}
          confirmText="Sí, deshacer"
        />
      )}

      <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: '1.25rem' }}>
        ← Volver
      </button>

      <div className="detail-card">
        <Gallery images={product.imageUrls} />

        <div className="detail-body">
          {/* Badges */}
          <div className="detail-meta">
            {product.category && <span className="badge badge-category">{product.category}</span>}
            {product.condition && (
              <span className={`badge ${product.condition === 'NEW' ? 'badge-new' : 'badge-used'}`}>
                {product.condition === 'NEW' ? 'Nuevo' : 'Usado'}
              </span>
            )}
            {product.status === 'ACTIVE' && product.madeToOrder && (
              <span className="badge badge-active">Se fabrica a demanda</span>
            )}
            {product.status === 'ACTIVE' && !product.madeToOrder && product.stock > 1 && (
              <span className="badge badge-active">{product.stock} disponibles</span>
            )}
            {product.status === 'ACTIVE' && !product.madeToOrder && (product.stock == null || product.stock <= 1) && (
              <span className="badge badge-active">Artículo único</span>
            )}
            {product.status === 'SOLD' && <span className="badge badge-sold">Vendido</span>}
            {product.status === 'PAUSED' && <span className="badge badge-paused">Pausado</span>}
          </div>

          <h1 className="detail-title">{product.title}</h1>

          {/* Made-to-order production time */}
          {product.madeToOrder && (
            <div className="info-box" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🔨</span>
              <span>
                {product.productionTimeValue
                  ? `Tiempo de fabricación: ${product.productionTimeValue} ${product.productionTimeUnit === 'WEEKS' ? 'semanas' : 'días'}`
                  : 'Consulte por tiempo de fabricación'}
              </span>
            </div>
          )}

          {/* Sold-to info */}
          {product.status === 'SOLD' && (
            <div className="info-box" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>✅</span>
              <span>
                {product.soldToUsername
                  ? <>Vendido a <UserLink
                      username={product.soldToUsername}
                      rating={product.soldToUserRating}
                      reviewCount={product.soldToUserReviewCount}
                    /></>
                  : 'Marcado como vendido'}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
            <div className="product-price-large">
              {product.priceOnConsultation
                ? 'Precio a consultar'
                : `${product.currency === 'USD' ? 'US$' : '$'}${Number(product.price).toLocaleString('es-UY')}`
              }
            </div>
            {/* Interest count — always public */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
              <span>❤️</span>
              <span>{interestCount} {interestCount === 1 ? 'interesado' : 'interesados'}</span>
            </div>
          </div>

          {product.description && <p className="detail-description">{product.description}</p>}

          {(product.department || product.city) && (
            <div className="detail-info-row">📍 {[product.city, product.department].filter(Boolean).join(', ')}</div>
          )}
          {deliveryLabel && <div className="detail-info-row">🚚 {deliveryLabel}</div>}
          {product.paymentMethod && <div className="detail-info-row">💳 {product.paymentMethod}</div>}

          {/* Characteristics */}
          {characteristics && Object.keys(characteristics).length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                Características
              </div>
              <table className="characteristics-table">
                <tbody>
                  {Object.entries(characteristics).map(([k, v]) => (
                    <tr key={k}><td>{k}</td><td>{v}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Video */}
          {product.videoUrl && (
            <div style={{ marginTop: '1.25rem' }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>Video</div>
              <video src={product.videoUrl} controls style={{ width: '100%', borderRadius: 'var(--radius-md)', maxHeight: 320 }} />
            </div>
          )}

          {/* Seller section */}
          <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border-subtle)' }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.75rem' }}>
              Vendedor
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                👤
              </div>
              <div>
                <Link to={`/profile/${product.sellerUsername}`} style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  {product.sellerUsername}
                </Link>
                {product.sellerProfession && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-cyan)' }}>{product.sellerProfession}</div>
                )}
                {product.sellerRating > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.2rem' }}>
                    <Stars rating={product.sellerRating} size="0.9rem" />
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {product.sellerRating.toFixed(1)} ({product.sellerReviewCount})
                    </span>
                  </div>
                )}
              </div>
            </div>

            {!isOwner && product.status === 'ACTIVE' && (
              <div className="detail-actions" style={{ paddingTop: 0, borderTop: 'none', flexWrap: 'wrap' }}>
                {/* Interest button */}
                <button
                  className={`btn ${interested ? 'btn-danger' : 'btn-secondary'}`}
                  onClick={handleInterest}
                  disabled={togglingInterest}
                >
                  {togglingInterest ? <span className="spinner" /> : interested ? '❤️ Me interesa' : '🤍 Me interesa'}
                </button>

                {/* Wishlist button */}
                <button
                  className={`btn ${wishlisted ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={(e) => { e.stopPropagation(); handleWishlist(); }}
                  disabled={togglingWishlist}
                >
                  {togglingWishlist ? <span className="spinner" /> : wishlisted ? '🔖 Guardado' : '📌 Guardar'}
                </button>

                {/* Agent IA button */}
                <button
                  className="btn"
                  onClick={() => navigate(`/agent?productId=${id}`)}
                  style={{
                    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                    border: 'none',
                    color: '#fff',
                  }}
                >
                  🤖 Preguntale al agente
                </button>

                <button className="btn btn-primary" onClick={handleContact} disabled={contacting}>
                  {contacting ? <><span className="spinner" /> Abriendo chat...</> : 'Contactar por chat'}
                </button>

                {waUrl && (
                  <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn contact-btn-wa">
                    WhatsApp
                  </a>
                )}
                {product.sellerPhone && (
                  <a href={`tel:${product.sellerPhone}`} className="btn btn-secondary">📞 Llamar</a>
                )}
                {product.sellerEmail && (
                  <a href={`mailto:${product.sellerEmail}`} className="btn btn-secondary">✉️ Email</a>
                )}
              </div>
            )}

            {/* Wishlist button — shown for non-active products or when user is not owner */}
            {!isOwner && product.status !== 'ACTIVE' && (
              <div style={{ marginTop: '0.75rem' }}>
                <button
                  className={`btn ${wishlisted ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={(e) => { e.stopPropagation(); handleWishlist(); }}
                  disabled={togglingWishlist}
                >
                  {togglingWishlist ? <span className="spinner" /> : wishlisted ? '🔖 Guardado' : '📌 Guardar'}
                </button>
              </div>
            )}
          </div>

          {/* Reviews section — collapsible */}
          <ReviewsSection
            sellerUsername={product.sellerUsername}
            currentUsername={user?.username}
          />

          {/* Transaction review section — for SOLD products */}
          {reviewStatus && (
            <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '1.25rem' }}>
              {reviewStatus.canReviewSeller && !reviewSubmitted.seller && (
                showReviewForm === 'seller' ? (
                  <ReviewForm
                    productId={Number(id)}
                    reviewedUsername={reviewStatus.sellerUsername}
                    onComplete={() => {
                      setShowReviewForm(null);
                      setReviewSubmitted((prev) => ({ ...prev, seller: true }));
                    }}
                    onCancel={() => setShowReviewForm(null)}
                  />
                ) : (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowReviewForm('seller')}
                  >
                    Calificar al vendedor
                  </button>
                )
              )}
              {reviewSubmitted.seller && (
                <div className="success-message">Calificación enviada al vendedor</div>
              )}

              {reviewStatus.canReviewBuyer && !reviewSubmitted.buyer && (
                showReviewForm === 'buyer' ? (
                  <ReviewForm
                    productId={Number(id)}
                    reviewedUsername={reviewStatus.buyerUsername}
                    onComplete={() => {
                      setShowReviewForm(null);
                      setReviewSubmitted((prev) => ({ ...prev, buyer: true }));
                    }}
                    onCancel={() => setShowReviewForm(null)}
                  />
                ) : (
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ marginTop: reviewStatus.canReviewSeller ? '0.5rem' : 0 }}
                    onClick={() => setShowReviewForm('buyer')}
                  >
                    Calificar al comprador
                  </button>
                )
              )}
              {reviewSubmitted.buyer && (
                <div className="success-message">Calificación enviada al comprador</div>
              )}

              {reviewStatus.buyerReviewedSeller && !reviewStatus.canReviewSeller && user?.username === reviewStatus.buyerUsername && !reviewSubmitted.seller && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  ✅ Ya calificaste al vendedor
                </div>
              )}
              {reviewStatus.sellerReviewedBuyer && !reviewStatus.canReviewBuyer && user?.username === reviewStatus.sellerUsername && !reviewSubmitted.buyer && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.3rem' }}>
                  ✅ Ya calificaste al comprador
                </div>
              )}
            </div>
          )}

          {/* Owner actions */}
          {isOwner && (
            <div className="detail-actions">
              <Link to={`/products/${id}/edit`} className="btn btn-secondary">Editar</Link>
              {product.status === 'SOLD' && (
                <button className="btn btn-secondary" onClick={handleUnsell} disabled={unselling}>
                  {unselling ? <><span className="spinner" /> Deshaciendo...</> : 'Deshacer venta'}
                </button>
              )}
            </div>
          )}

          {/* Share buttons */}
          <ShareButtons product={product} canonicalUrl={canonicalUrl} />

          {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}
        </div>
      </div>

      {/* Wishlist toast — rendered via portal to avoid overflow:hidden */}
      {wishlistTooltip && ReactDOM.createPortal(
        <div className="wishlist-toast">
          🔖 Guardado en tu <strong>Lista de deseos</strong> del menú lateral
        </div>,
        document.body
      )}
    </div>
  );
}

export default ProductDetail;
